import { useState } from "react";

import { SectionShell } from "./SectionShell";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToastType } from "@/enums";
import {
  IdentitySettings,
  useUpdateIdentitySettings,
} from "@/queries/identity/useIdentitySettings";
import { useWorkosOrganizations } from "@/queries/identity/useWorkosOrganizations";
import { displayToast } from "@/utils/toast";

interface Props {
  organizationId: number;
  settings: IdentitySettings;
  onError: (msg: string | null) => void;
}

/**
 * On-prem only: when multiple WorkOS organizations exist and none is linked
 * yet, let the owner pick which WorkOS org backs this PromptLayer org.
 */
export const WorkOsOrganizationSection = ({ organizationId, settings, onError }: Props) => {
  const needsSelection = !settings.workos_organization_id;
  const { data, isLoading } = useWorkosOrganizations(organizationId, needsSelection);
  const updateSettings = useUpdateIdentitySettings(organizationId);
  const [selectedId, setSelectedId] = useState<string>("");

  const options = data?.items ?? [];
  if (!needsSelection || isLoading || options.length <= 1) {
    return null;
  }

  const handleLink = () => {
    if (!selectedId) {
      onError("Select a WorkOS organization to link.");
      return;
    }
    onError(null);
    updateSettings.mutate(
      { workos_organization_id: selectedId },
      {
        onSuccess: () => {
          displayToast("WorkOS organization linked.", ToastType.success);
        },
        onError: () => onError("Could not link the selected WorkOS organization. Try again."),
      },
    );
  };

  return (
    <SectionShell
      title="Link WorkOS organization"
      description="Multiple WorkOS organizations were found in your environment. Choose which one should back this PromptLayer organization before configuring SSO."
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-[280px] flex-1">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="h-9 w-full">
              <SelectValue placeholder="Select a WorkOS organization" />
            </SelectTrigger>
            <SelectContent>
              {options.map(org => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name} ({org.id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleLink} disabled={!selectedId || updateSettings.isPending}>
          {updateSettings.isPending ? "Linking…" : "Link organization"}
        </Button>
      </div>
    </SectionShell>
  );
};
